import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const KeyboardContext = createContext(null);

export const useKeyboard = () => useContext(KeyboardContext);

// Storage key for persisting keyboard setting
const KEYBOARD_SETTING_KEY = 'kiosk_keyboard_disabled';

// Read keyboard=on | keyboard=off from URL (case-insensitive). No param = default ON.
const readKeyboardSettingFromURL = () => {
  try {
    let urlParam = null;
    const search = window.location.search || '';
    if (search) {
      const params = new URLSearchParams(search);
      urlParam = params.get('keyboard');
    }
    if (urlParam == null || urlParam === '') {
      // Fallback: parse from full href (e.g. if search was empty on first paint)
      const href = window.location.href || '';
      const q = href.indexOf('?');
      if (q >= 0) {
        const params = new URLSearchParams(href.slice(q));
        urlParam = params.get('keyboard');
      }
    }
    if (urlParam == null || urlParam === '') return null;
    const value = String(urlParam).trim().toLowerCase();
    if (value === 'off') return true;   // disabled
    if (value === 'on') return false;   // enabled
    return false; // any other value: default ON
  } catch (error) {
    console.error('[Keyboard] Error reading keyboard setting from URL:', error);
    return null;
  }
};

// Default: keyboard ON. If URL has keyboard=on or keyboard=off, use that; otherwise default ON.
const getKeyboardDisabledState = () => {
  try {
    const urlSetting = readKeyboardSettingFromURL();
    if (urlSetting !== null) {
      return urlSetting; // true = disabled, false = enabled
    }
    // No URL param: default keyboard ON (enabled)
    return false;
  } catch (error) {
    console.error('[Keyboard] Error getting keyboard disabled state:', error);
    return false; // Default: keyboard on
  }
};

// Supported types: 'numeric' | 'alpha' | 'email'
// Optional NumericKeypadComponent / AlphaKeyboardComponent: when passed (e.g. from index), keyboard is in main bundle so production never fails on lazy chunk load
export const KeyboardProvider = ({ children, NumericKeypadComponent, AlphaKeyboardComponent }) => {
  // TEST: Log immediately when component loads
  console.log('🔵 [Keyboard] KeyboardProvider component loaded!');
  console.log('🔵 [Keyboard] Current URL:', window.location.href);
  console.log('🔵 [Keyboard] Search params:', window.location.search);
  
  const [visible, setVisible] = useState(false);
  const [suppressUntil, setSuppressUntil] = useState(0);
  const [type, setType] = useState('numeric');
  const [options, setOptions] = useState({}); // e.g., { allowDecimal, maxLength }
  const [handlers, setHandlers] = useState({
    onInput: null,    // (text) => void
    onBackspace: null,// () => void
    onEnter: null,    // () => void
    onClose: null,    // () => void
    onClear: null     // () => void
  });

  // Read keyboard setting ONCE on initial mount and store it
  // This setting persists throughout the session regardless of URL changes
  const [keyboardDisabled, setKeyboardDisabled] = useState(() => {
    const result = getKeyboardDisabledState();
    console.log('🔵 [Keyboard] Initial keyboardDisabled state:', result);
    return result;
  });

  // Sync state from URL on mount and after short delays (kiosk/redirect may set URL after first paint)
  React.useEffect(() => {
    const apply = () => {
      const result = getKeyboardDisabledState();
      setKeyboardDisabled(result);
      console.log('[Keyboard] ✅ Keyboard setting from URL:', result ? 'DISABLED' : 'ENABLED');
    };
    apply();
    const t1 = setTimeout(apply, 100);
    const t2 = setTimeout(apply, 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Prefetch keyboard modules in the background so the first open feels instant
  React.useEffect(() => {
    // webpackPrefetch hints help when supported
    import(/* webpackPrefetch: true */ '../components/common/keyboard/NumericKeypad');
    import(/* webpackPrefetch: true */ '../components/common/keyboard/AlphaKeyboard');
  }, []);

  const open = useCallback((kbType = 'numeric', opts = {}, h = {}) => {
    // Check the stored keyboard disabled state (set once on initial load)
    console.log('[Keyboard] open() called. Current keyboardDisabled state:', keyboardDisabled);
    
    // If keyboard is disabled (from initial URL parameter), don't open
    // Physical keyboard input will work automatically through normal input field onChange handlers
    if (keyboardDisabled) {
      console.warn('[Keyboard] ❌❌❌ Keyboard is DISABLED (from initial setting), NOT opening virtual keyboard');
      console.log('[Keyboard] ✅ Physical keyboard input is ENABLED and will work normally through input field onChange handlers');
      return; // EARLY RETURN - don't do anything else, physical keyboard works automatically
    }
    
    console.log('[Keyboard] ✅ Keyboard is ENABLED, opening...');
    
    // Prevent immediate re-open after a dialog closes or explicit suppression
    const now = Date.now();
    if (now < suppressUntil) {
      console.log('[Keyboard] Suppressed until:', new Date(suppressUntil).toISOString());
      return;
    }
    
    // Only set state if we get here (keyboard is enabled)
    setType(kbType);
    setOptions(opts || {});
    setHandlers({
      onInput: h.onInput || null,
      onBackspace: h.onBackspace || null,
      onEnter: h.onEnter || null,
      onClose: h.onClose || null,
      onClear: h.onClear || null
    });
    setVisible(true);
    console.log('[Keyboard] ✅✅✅ Keyboard state set to VISIBLE');
  }, [suppressUntil, keyboardDisabled]);

  const close = useCallback(() => {
    setVisible(false);
    handlers.onClose && handlers.onClose();
  }, [handlers]);

  const api = useMemo(() => ({
    visible,
    type,
    options,
    open,
    close,
    keyboardDisabled,
    NumericKeypadComponent,
    AlphaKeyboardComponent,
    suppress: (ms = 500) => {
      try {
        const until = Date.now() + Math.max(0, ms);
        setSuppressUntil(until);
      } catch {}
    },
    // Pass-through handlers
    onInput: (ch) => handlers.onInput && handlers.onInput(ch),
    onBackspace: () => handlers.onBackspace && handlers.onBackspace(),
    onEnter: () => {
      if (handlers.onEnter) {
        return handlers.onEnter();
      }
      // Default behavior: close keyboard on Enter for user-friendly flows
      try { close(); } catch (_) {}
    },
    onClear: () => {
      if (handlers.onClear) {
        return handlers.onClear();
      }
      // Global fallback: clear currently focused input/textarea/contenteditable
      try {
        const el = document.activeElement;
        if (!el) return;
        const tag = (el.tagName || '').toLowerCase();
        const isInput = tag === 'input' || tag === 'textarea';
        const isEditable = !!el.isContentEditable;
        if (isInput) {
          el.value = '';
          // Dispatch an input event so React controlled components receive update
          const ev = new Event('input', { bubbles: true });
          el.dispatchEvent(ev);
        } else if (isEditable) {
          el.textContent = '';
          const ev = new Event('input', { bubbles: true });
          el.dispatchEvent(ev);
        }
      } catch (e) {
        // no-op fallback
      }
    },
  }), [visible, type, options, open, close, handlers, keyboardDisabled, NumericKeypadComponent, AlphaKeyboardComponent]);

  return (
    <KeyboardContext.Provider value={api}>
      {children}
    </KeyboardContext.Provider>
  );
};

// Minimal error boundary so lazy keyboard chunk load failures don't hide the overlay silently
class KeyboardChunkErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) {
    console.error('[Keyboard] Chunk or render error:', err?.message || err, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>
          Keyboard could not load. Try again.
        </div>
      );
    }
    return this.props.children;
  }
}

// Global overlay that decides which keyboard to show
export const KeyboardOverlay = () => {
  const kb = useKeyboard();
  
  // If keyboard is disabled (from URL), don't render overlay
  if (kb?.keyboardDisabled) {
    if (kb?.visible) {
      try { kb?.close?.(); } catch (e) {}
    }
    return null;
  }
  
  if (!kb?.visible) return null;

  // Backdrop that covers the page for click-to-close
  const backdropStyle = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    background: 'transparent',
    zIndex: 9999,
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    // Allow taps to pass through to underlying app content
    pointerEvents: 'none'
  };

  // Smart positioning near the focused element
  const panelRef = React.useRef(null);
  const [panelPos, setPanelPos] = React.useState({ top: null, left: null, width: null, height: null });

  // Reposition logic exposed for reuse
  const reposition = React.useRef(() => {}).current;

  React.useEffect(() => {
    // Compute target anchor (focused input)
    const el = document.activeElement;
    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const padding = 8; // gap to screen edges
    const anchorGapPx = Number.isFinite(kb?.options?.anchorOffsetPx) ? kb.options.anchorOffsetPx : 100; // extra gap below input
    const widthPct = (kb?.options?.widthPct ?? 0.9);
    const heightVh = (kb?.options?.heightVh ?? 45); // percent of viewport height
    const desiredWidth = Math.min(1200, Math.floor(viewportW * Math.max(0.5, Math.min(widthPct, 0.95))));
    const desiredHeight = Math.floor(viewportH * Math.max(0.25, Math.min(heightVh / 100, 0.6)));

    // Measure panel (after first render it exists)
    const measureAndPlace = () => {
      const panelW = desiredWidth;
      const preferred = kb?.options?.position || 'auto'; // 'auto' | 'center' | 'below' | 'above' | 'bottom'
      const autoHeight = kb?.options?.autoHeight === true;
      const panelH = autoHeight ? null : (desiredHeight || 320);

      let top = null;
      let left = Math.max(padding, Math.floor((viewportW - panelW) / 2)); // default center horizontally

      // If an explicit anchor selector is supplied, position relative to that element (below it)
      const anchorSelector = kb?.options?.anchorSelector;
      if (anchorSelector) {
        const anchorEl = document.querySelector(anchorSelector);
        if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
          const r = anchorEl.getBoundingClientRect();
          const estH = (desiredHeight || 320); // estimate when autoHeight true
          top = Math.min(viewportH - estH - padding, Math.max(padding, Math.floor(r.bottom + anchorGapPx)));
          // Horizontal position: either viewport center (when hCenter) or anchor-centered
          if (kb?.options?.hCenter) {
            left = Math.max(padding, Math.floor((viewportW - panelW) / 2));
          } else {
            const centerX = r.left + r.width / 2;
            left = Math.floor(centerX - panelW / 2);
            left = Math.max(padding, Math.min(left, viewportW - panelW - padding));
          }
          setPanelPos({ top, left, width: panelW, height: autoHeight ? null : (desiredHeight || 'auto') });
          return;
        }
      }

      if (preferred === 'center') {
        // Center vertically. If autoHeight, we will rely on CSS translate to center after render.
        top = autoHeight ? null : Math.max((viewportH - (panelH || 0)) / 2, padding);
      } else if (preferred === 'lowerCenter') {
        // Middle of lower half (~75% of viewport), center vertically around that point
        top = autoHeight ? null : Math.max(padding, Math.min(viewportH - (panelH || 0) - padding, Math.floor(viewportH * 0.75 - (panelH || 0) / 2)));
      } else if (preferred === 'bottom') {
        top = Math.max(padding, viewportH - panelH - padding);
      } else if (el && typeof el.getBoundingClientRect === 'function') {
        const r = el.getBoundingClientRect();
        // Prefer below the field if there's room; otherwise place above
        const spaceBelow = viewportH - r.bottom - padding;
        const spaceAbove = r.top - padding;
        if (spaceBelow >= panelH) {
          top = Math.min(viewportH - panelH - padding, Math.max(padding, Math.floor(r.bottom + anchorGapPx)));
        } else if (spaceAbove >= panelH) {
          top = Math.max(padding, Math.floor(r.top - panelH - anchorGapPx));
        } else {
          // Not enough space fully above or below; pin to bottom with padding
          top = viewportH - panelH - padding;
        }
        // Horizontal position: either viewport center (when hCenter) or input-centered
        if (kb?.options?.hCenter) {
          left = Math.max(padding, Math.floor((viewportW - panelW) / 2));
        } else {
          const inputCenterX = r.left + r.width / 2;
          left = Math.floor(inputCenterX - panelW / 2);
          left = Math.max(padding, Math.min(left, viewportW - panelW - padding));
        }
      } else {
        // No active element; dock to bottom center
        top = viewportH - 320 - padding;
      }

      setPanelPos({ top, left, width: panelW, height: panelH });
    };

    // run after mount and on resize/scroll
    measureAndPlace();
    // store reposition for external callers
    // NOTE: assign to ref via side effect
    (repositionRef => { repositionRef.current = measureAndPlace; })(repositionRef);
    const onResize = () => measureAndPlace();
    const onScroll = () => measureAndPlace();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [kb.visible, kb.type]);

  // Allow external reposition via kb.reposition()
  const repositionRef = React.useRef(null);
  React.useEffect(() => {
    if (!kb) return;
    kb.reposition = () => {
      try { repositionRef.current && repositionRef.current(); } catch {}
    };
    return () => { if (kb.reposition) kb.reposition = null; };
  }, [kb]);

  // Optionally, scroll the active input into view when keyboard opens
  React.useEffect(() => {
    if (!kb.visible) return;
    try {
      const el = document.activeElement;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      }
    } catch {}
  }, [kb.visible]);

  // Follow focus: when the keyboard is visible and focus changes to another input, just reposition
  React.useEffect(() => {
    if (!kb.visible) return;
    const onFocusIn = (e) => {
      try {
        const target = e.target;
        if (!target) return;
        const tag = (target.tagName || '').toLowerCase();
        const isInput = tag === 'input' || tag === 'textarea' || target.isContentEditable;
        if (isInput) {
          repositionRef.current && repositionRef.current();
          if (typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
          }
        }
      } catch {}
    };
    window.addEventListener('focusin', onFocusIn, true);
    return () => window.removeEventListener('focusin', onFocusIn, true);
  }, [kb.visible]);

  const isCenter = kb?.options?.position === 'center';
  const isLowerCenter = kb?.options?.position === 'lowerCenter';
  const hCenter = kb?.options?.hCenter === true;
  const autoHeight = kb?.options?.autoHeight === true;
  const panelStyle = {
    position: 'fixed',
    top: isCenter
      ? (autoHeight ? '50%' : (panelPos.top ?? '50%'))
      : (isLowerCenter
          ? (autoHeight ? '75%' : (panelPos.top ?? '75%'))
          : (panelPos.top ?? 'auto')),
    left: (isCenter || isLowerCenter || hCenter) ? '50%' : (panelPos.left ?? '50%'),
    transform: (
      isCenter || isLowerCenter
        ? (autoHeight ? 'translate(-50%, -50%)' : 'translateX(-50%)')
        : (hCenter ? 'translateX(-50%)' : (panelPos.left == null ? 'translateX(-50%)' : 'none'))
    ),
    width: panelPos.width || '90%',
    height: autoHeight ? 'auto' : (panelPos.height || 'auto'),
    maxWidth: 1200,
    background: '#e9e9e9',
    border: '1px solid #bdbdbd',
    borderRadius: 12,
    boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  // Use components from provider (main bundle) when passed; else lazy (can 404 in production)
  const NumericKeypad = kb?.NumericKeypadComponent || LazyNumericKeypad;
  const AlphaKeyboard = kb?.AlphaKeyboardComponent || LazyAlphaKeyboard;

  return (
    <div style={backdropStyle}>
      <div ref={panelRef} style={{ ...panelStyle, pointerEvents: 'auto' }}>
        <KeyboardChunkErrorBoundary>
          {kb.type === 'numeric' ? (
            <NumericKeypad />
          ) : (
            <AlphaKeyboard />
          )}
        </KeyboardChunkErrorBoundary>
      </div>
    </div>
  );
};

// Fallback when not passed from index (lazy can fail to load on VM/production)
const LazyNumericKeypad = React.lazy(() => import('../components/common/keyboard/NumericKeypad'));
const LazyAlphaKeyboard = React.lazy(() => import('../components/common/keyboard/AlphaKeyboard'));

// (Prev/Next/Done toolbar removed as requested)
